import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "ripple-pool";

const DEFAULT_BG_TOP = "#0c1f2c";
const DEFAULT_BG_BOTTOM = "#0e2c3f";
const DEFAULT_RIPPLE = "#67e8f9";

const SPAWN_INTERVAL_MS = 800;   // base time between drops (slower cadence)
const SPAWN_JITTER_MS = 700;     // random extra delay so the rhythm feels natural
const RIPPLE_SPEED = 45;         // px per second the wavefront expands
const RIPPLE_LIFE_MS = 7000;     // how long each ripple lives before fading out
const RING_COUNT = 3;            // concentric rings per ripple drop
const RING_GAP = 14;             // px between consecutive rings
const MAX_CONCURRENT = 14;       // hard cap so we never thrash the GPU

function rand(min, max) { return min + Math.random() * (max - min); }

export default function RipplePool({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const ripplesRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG_TOP, DEFAULT_BG_BOTTOM, DEFAULT_RIPPLE]);
  const lastSpawnRef = useRef(0);
  const nextSpawnDelayRef = useRef(0);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG_TOP),
      pickColor(colors, 1, DEFAULT_BG_BOTTOM),
      pickColor(colors, 2, DEFAULT_RIPPLE),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const spawnDrop = (ts) => {
      ripplesRef.current.push({
        x: rand(0, width),
        y: rand(0, height),
        startTs: ts,
        // Slight per-drop variance so adjacent ripples never look identical.
        speed: RIPPLE_SPEED * rand(0.85, 1.15),
        life: RIPPLE_LIFE_MS * rand(0.85, 1.2),
      });
    };

    const tick = (ts) => {
      const [bgTop, bgBottom, rippleColor] = colorsRef.current;

      // Background (still water) — vertical gradient gives a subtle depth.
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, bgTop);
      grad.addColorStop(1, bgBottom);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Spawn a new drop on cadence.
      if (lastSpawnRef.current === 0) {
        lastSpawnRef.current = ts;
        nextSpawnDelayRef.current = SPAWN_INTERVAL_MS + Math.random() * SPAWN_JITTER_MS;
      }
      if (
        ts - lastSpawnRef.current > nextSpawnDelayRef.current &&
        ripplesRef.current.length < MAX_CONCURRENT
      ) {
        spawnDrop(ts);
        lastSpawnRef.current = ts;
        nextSpawnDelayRef.current = SPAWN_INTERVAL_MS + Math.random() * SPAWN_JITTER_MS;
      }

      // Render + cull ripples.
      ctx.lineCap = "round";
      const ripples = ripplesRef.current;
      for (let i = ripples.length - 1; i >= 0; i -= 1) {
        const r = ripples[i];
        const age = ts - r.startTs;
        if (age > r.life) {
          ripples.splice(i, 1);
          continue;
        }

        const ageSec = age / 1000;
        const lifeT = age / r.life; // 0..1
        // Each drop emits RING_COUNT concentric rings; the head ring leads,
        // the others trail behind by RING_GAP px each. They all share the
        // same outward velocity, which gives the concentric look you see in
        // a real water ripple.
        for (let k = 0; k < RING_COUNT; k += 1) {
          const radius = ageSec * r.speed - k * RING_GAP;
          if (radius <= 0) continue;
          // Fade two ways: with age (overall life) and with ring index
          // (older trailing rings are softer than the head).
          const ringFade = Math.max(0, 1 - lifeT);
          const indexFade = 1 - k / RING_COUNT;
          const alpha = 0.45 * ringFade * indexFade;
          if (alpha <= 0.01) continue;

          ctx.lineWidth = 1.4 - k * 0.25;
          ctx.strokeStyle = rgba(rippleColor, alpha);
          ctx.beginPath();
          ctx.arc(r.x, r.y, radius, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Tiny disc at the drop point for the first instant — sells the
        // "drop just hit the water" moment.
        if (age < 220) {
          const a = (1 - age / 220) * 0.5;
          ctx.fillStyle = rgba(rippleColor, a);
          ctx.beginPath();
          ctx.arc(r.x, r.y, 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
